/// <reference path="module.ts" />

/**
 * Appointment Book — Web Component Bootstrap
 *
 * Single-call registration with the generic web component framework.
 * The framework handles: loader creation, gate evaluation, bundle loading,
 * element mounting, bridge injection, and legacy fallback — all automatically.
 */
angular.module('ApptBookApp').run([
    '$rootScope',
    'meevo.AppSettings',
    'meevo.components.common.BusinessInfoDataService',
    'meevo.plugins.interop.InteropService',
    'webComponentSetup',
    '$mdSidenav',
    '$state',
    function (
        $rootScope: angular.IRootScopeService,
        appSettings: Meevo.Components.Common.IAppSettings,
        businessInfoDataService: Meevo.Components.Common.IBusinessInfoDataService,
        interopService: Meevo.Plugins.Interop.InteropService,
        webComponentSetup: any,
        $mdSidenav: ng.material.ISidenavService,
        $state: angular.ui.IStateService,
    ) {
        // Holds a reference to the mounted custom element so that the
        // APPLY_PARAMS_TO_CONTEXT listener below can dispatch DOM events to it.
        var mountedWcElement: HTMLElement | null = null;

        // Buffer for convo params that arrive before the web component mounts.
        // APPLY_PARAMS_TO_CONTEXT fires during the route transition, before the bundle
        // finishes loading and onMount is called. We store the normalised detail here
        // and replay it in onMount so no params are silently dropped.
        var pendingConvoDetail: any = null;

        // Listen for convobar / context navigation params broadcast by main.controller.
        // When the appointment book is the target state, normalise all resolved params
        // (date, view mode, employee/gender/price-level filters, time scroll) and forward
        // them as a single meevo:apply-convo-params CustomEvent to the mounted web component.
        function pad2(n: number): string {
            return n < 10 ? '0' + n : '' + n;
        }

        function toLocalIsoDate(d: Date): string {
            return d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate());
        }

        function dispatchConvoDetail(wcElement: HTMLElement, detail: any): void {
            wcElement.dispatchEvent(new CustomEvent('meevo:apply-convo-params', {
                detail: detail,
                bubbles: false,
            }));
        }

        $rootScope.$on(Meevo.Components.Common.Messages.APPLY_PARAMS_TO_CONTEXT, function (event, state, params) {
            if (!state || state.name !== appSettings.ROUTE_NAMES.appointmentBook) {
                return;
            }
            if (!params) {
                return;
            }

            // ── Date ─────────────────────────────────────────────────
            var targetDate: string | null = null;

            if (params.dates && params.dates.length > 0) {
                var raw = params.dates[0];
                // A bare 'YYYY-MM-DD' must NOT be round-tripped through `new Date(...)`:
                // the ES spec parses date-only strings as UTC midnight, so any browser
                // west of UTC re-formats it back to the previous calendar day. Forward
                // the canonical local date verbatim instead. (Same guard used by
                // navigateToScheduleManager below.)
                if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                    targetDate = raw;
                } else {
                    var d = raw instanceof Date ? raw : new Date(raw);
                    if (!isNaN(d.getTime())) {
                        targetDate = toLocalIsoDate(d);
                    }
                }
            }

            if (!targetDate && params.dateAction) {
                var now = new Date();
                if (params.dateAction === 'tomorrow') {
                    now.setDate(now.getDate() + 1);
                }
                // Only 'tomorrow' offsets the date; 'today' and any unrecognised
                // dateAction values resolve to the current date (safe fallback).
                targetDate = toLocalIsoDate(now);
            }

            // ── View mode ────────────────────────────────────────────
            var viewMode: 'day' | 'week' = (params.dates && params.dates.length > 1) ? 'week' : 'day';

            // ── Employees ────────────────────────────────────────────
            // clearFilters mirrors legacy: absent employees → clear all filters
            // empty array → set empty employee filter only
            var clearFilters: boolean = (params.employees == null);
            var employeeIds: string[] = (params.employees || []).map(function (e: any) { return String(e.id); });

            // ── Gender ───────────────────────────────────────────────
            // Server sends GenderPreferenceEnum: 108 = MaleOnly, 109 = FemaleOnly
            var gender: number | null = (params.gender != null) ? params.gender : null;

            // ── Price levels ─────────────────────────────────────────
            var priceLevels: number[] = params.priceLevels || [];

            // ── Time scroll ──────────────────────────────────────────
            var time: string | null = params.time || null;
            // Legacy param name is scrollToCurrentDate; outgoing contract uses scrollToCurrentTime.
            var scrollToCurrentTime: boolean = !!params.scrollToCurrentDate;

            var detail = {
                date: targetDate,
                viewMode: viewMode,
                employeeIds: employeeIds,
                clearFilters: clearFilters,
                gender: gender,
                priceLevels: priceLevels,
                time: time,
                scrollToCurrentTime: scrollToCurrentTime,
                columnHeaderOrder: params.columnHeaderOrder || null,
            };

            // Guard: if the element was removed from the DOM (user closed the
            // appointment book tab), mountedWcElement is orphaned — isConnected
            // returns false. Clear it so we fall through to the pending-detail
            // buffer path; onMount will replay the params when the component
            // remounts.
            if (mountedWcElement && !(mountedWcElement as any).isConnected) {
                mountedWcElement = null;
            }

            if (mountedWcElement) {
                // Component already mounted — defer out of the current AngularJS
                // $broadcast/$digest cycle before dispatching. Firing synchronously
                // inside $broadcast can interfere with Angular's zone and signal
                // reactivity, causing the date/filter updates to be silently dropped.
                // This mirrors the legacy $timeout() pattern in AppointmentBook.ts.
                var wcEl = mountedWcElement;
                setTimeout(function () { dispatchConvoDetail(wcEl, detail); }, 0);
            } else {
                // Component not yet mounted (bundle still loading or tab was
                // closed and is remounting). Buffer the detail; onMount will
                // replay it once the element exists and its event listener
                // (registered in ngOnInit) is attached.
                pendingConvoDetail = detail;
            }
        });

        webComponentSetup.register({
            id: 'appointment-book',

            // ── Feature Gate ────────────────────────────────────────────
            gateSource: 'businessInfoPreferences',
            gateResolver: function () {
                return businessInfoDataService.getBusinessInfoPreferences();
            },

            // ── Pre-Mount Props ─────────────────────────────────────────
            // Returns buffered convo params (if any) as initialConvoParams.
            // Called by the mount directive BEFORE appendChild, so the
            // component has the correct date/filters from its very first
            // initialization cycle — no flash, no wasted API call.
            preMountProps: function () {
                if (!pendingConvoDetail) {
                    return null;
                }
                var detail = pendingConvoDetail;
                pendingConvoDetail = null;
                return { initialConvoParams: detail };
            },

            // ── Post-Mount Hooks ────────────────────────────────────────
            onMount: function (wcElement: HTMLElement) {
                mountedWcElement = wcElement;

                // R-121880: The AB emits { date: 'YYYY-MM-DD', viewMode } on
                // scheduleEmployeeClick. The Schedule Manager runs in an interop iframe
                // child app, so we hand the date off via the generic host→child context
                // params pipe (InteropService.pushParamsToContext → xplat
                // HostContextParamsService). String detail (legacy ISO) and bare
                // no-detail calls fall through to the child's default (today).
                //
                // A plain 'YYYY-MM-DD' is intentionally forwarded as-is rather than
                // round-tripped through `new Date(...)` + toLocalIsoDate(), because
                // JavaScript parses bare date-only strings as UTC midnight. In any
                // local timezone west of UTC that re-local-formats to the previous
                // day — which would make the Schedule Manager open to the prior week.
                var DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
                function navigateToScheduleManager(detail?: any) {
                    var schedulesRoute: string = appSettings.ROUTE_NAMES.schedulesManager;
                    var rawDate: string | null = null;

                    if (typeof detail === 'string' && detail.length > 0) {
                        rawDate = detail;
                    } else if (detail && typeof detail === 'object') {
                        rawDate = (detail as any).date || (detail as any).currentDate || null;
                    }

                    if (rawDate) {
                        var currentDate: string | null = null;
                        if (DATE_ONLY_PATTERN.test(rawDate)) {
                            // Already a canonical local date — forward verbatim.
                            currentDate = rawDate;
                        } else {
                            var parsed = new Date(rawDate);
                            if (!isNaN(parsed.getTime())) {
                                currentDate = toLocalIsoDate(parsed);
                            }
                        }
                        if (currentDate) {
                            // Cold-open transport: embed date in the iframe URL
                            // querystring so the child reads it synchronously on
                            // boot — no post-robot handshake race.
                            interopService.setQueryParams(schedulesRoute, {
                                currentDate: currentDate,
                            });
                            // Warm-redirect transport: push via post-robot for
                            // the case where the iframe is already alive.
                            interopService.pushParamsToContext(schedulesRoute, {
                                currentDate: currentDate,
                            });
                        }
                    }

                    $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                        stateName: schedulesRoute,
                    });
                }

                // Angular Elements lowercases event names; listen for both casings
                wcElement.addEventListener('scheduleEmployeeClick', function (event: Event) {
                    navigateToScheduleManager((event as CustomEvent).detail);
                });
                wcElement.addEventListener('scheduleemployeeclick', function (event: Event) {
                    navigateToScheduleManager((event as CustomEvent).detail);
                });

                // R-VIEW-019: Send message — routes to MeevoMessenger (Textel/TwoWaySMS)
                // or Client Find's Send Text Message tab when no messaging integration is
                // active. The WC currently dispatches the camelCase name via
                // `dispatchEvent(new CustomEvent('sendMessage', ...))` — register both
                // casings (matches the scheduleEmployeeClick pattern above) so the
                // listener fires regardless of whether the dispatch side is normalised.
                function handleSendMessage(event: Event) {
                    const d = (event as CustomEvent).detail as {
                        clientId: string;
                        firstName: string | null;
                        lastName: string | null;
                        primaryPhoneNumber: string | null;
                        countryCode: string | null;
                        isRestricted: boolean;
                        isClient: boolean;
                        classification: 'TEXTEL' | 'SMS' | null;
                    };

                    $rootScope.$apply(function () {
                        if (d.classification) {
                            // Textel or TwoWaySMS — open MeevoMessenger
                            $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                                stateName: appSettings.ROUTE_NAMES.meevoMessenger,
                                stateParams: {
                                    id:                 d.clientId,
                                    firstName:          d.firstName,
                                    lastName:           d.lastName,
                                    primaryPhoneNumber: d.primaryPhoneNumber,
                                    countryCode:        d.countryCode,
                                    isRestricted:       d.isRestricted,
                                    isClient:           d.isClient,
                                    classification:     d.classification,
                                },
                            });
                        } else {
                            // No messaging integration — open Search sidenav then
                            // broadcast to Client Find "Send Text Message" tab.
                            // Mirrors legacy: sideContextMenu('Search').open().then(…)
                            $mdSidenav('Search').open().then(function () {
                                $rootScope.$broadcast(Meevo.Components.Common.Messages.OPEN_SEARCH_MENU_COMPLETE, {
                                    id: d.clientId,
                                    landingTab: Meevo.Model.NounEnum.ClientFindSendTextMsgTab,
                                });
                            });
                        }
                    });
                }
                wcElement.addEventListener('sendMessage', handleSendMessage);
                wcElement.addEventListener('sendmessage', handleSendMessage);

                // Launch form — mirrors legacy
                // `docsAndFormsService.showForm → GOTO_STATE(docsAndFormsKiosk)`
                // so the WC's per-form "Launch form" action opens the same
                // kiosk view that the legacy view-appointment popup uses.
                function handleLaunchForm(event: Event) {
                    const d = (event as CustomEvent).detail as {
                        formId: string;
                        formDisplayName: string;
                        formUrl: string;
                        clientId: string;
                        appointmentId: string;
                        appointmentServiceId: string;
                    };
                    if (!d || !d.formId) {
                        return;
                    }

                    $rootScope.$apply(function () {
                        $rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                            stateName: appSettings.ROUTE_NAMES.docsAndFormsKiosk,
                            stateParams: {
                                form: {
                                    formDisplayName: d.formDisplayName,
                                    formId:          d.formId,
                                    formUrl:         d.formUrl,
                                    serviceId:       d.appointmentServiceId,
                                },
                                clientId:      d.clientId,
                                appointmentId: d.appointmentId,
                                callbackState: $state.current.name,
                            },
                        });
                    });
                }
                wcElement.addEventListener('launchForm', handleLaunchForm);
                wcElement.addEventListener('launchform', handleLaunchForm);
            },

            // ── Pre-Unmount Hooks ───────────────────────────────────────
            // Runs just before the mount directive removes the element (route
            // away / $scope destroy). Element-scoped addEventListeners above
            // die with the element, so they don't need explicit teardown —
            // but the host-side `mountedWcElement` reference does. Clearing
            // it here ensures the APPLY_PARAMS_TO_CONTEXT listener falls
            // through to the pendingConvoDetail buffer path on the next
            // convo navigation instead of dispatching to a detached element.
            onUnmount: function (wcElement: HTMLElement) {
                if (mountedWcElement === wcElement) {
                    mountedWcElement = null;
                }
            },
        });
    }
]);
